import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError } from '../../core/api.service';
import { I18nService } from '../../core/i18n.service';
import { SessionService } from '../../core/session.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './auth-callback.html'
})
export class AuthCallback implements OnInit {
  readonly errorMessage = signal('');
  readonly errorMessageKey = signal('');
  readonly providerLabel = signal('OAuth');
  readonly needsXEmail = signal(false);
  xEmail = '';
  isSubmittingEmail = false;

  constructor(
    public i18n: I18nService,
    private route: ActivatedRoute,
    private router: Router,
    public session: SessionService
  ) {}

  async ngOnInit(): Promise<void> {
    this.providerLabel.set(this.session.oauthCallbackProviderLabel());
    const query = this.route.snapshot.queryParamMap;
    const error = query.get('error');
    const errorDescription = query.get('error_description') || query.get('error_message') || query.get('error_code');
    if (error) {
      this.session.showLoginError(errorDescription || error);
      await this.router.navigateByUrl('/login');
      return;
    }

    const code = query.get('code');
    const state = query.get('state');
    if (!code || !state) {
      this.errorMessage.set('Missing OAuth callback parameters. Please try signing in again.');
      return;
    }

    try {
      const redirectTo = await this.session.completeOAuthCallback(code, state);
      if (redirectTo.startsWith('/auth/callback?x_email_required=1')) {
        this.needsXEmail.set(true);
        return;
      }
      await this.router.navigateByUrl(redirectTo || '/');
    } catch (callbackError) {
      this.setCallbackError(callbackError, `${this.providerLabel()} sign-in failed.`);
    }
  }

  async submitXEmail(): Promise<void> {
    if (this.isSubmittingEmail) return;
    const email = this.xEmail.trim().toLowerCase();
    if (!email) return;
    this.isSubmittingEmail = true;
    try {
      const redirectTo = await this.session.completeXOAuthCallbackWithEmail(email);
      await this.router.navigateByUrl(redirectTo || '/');
    } catch (callbackError) {
      this.setCallbackError(callbackError, 'X sign-in failed.');
    } finally {
      this.isSubmittingEmail = false;
    }
  }

  private setCallbackError(error: unknown, fallback: string): void {
    if (error instanceof ApiError && error.messageKey) {
      this.errorMessageKey.set(error.messageKey);
      this.errorMessage.set('');
      return;
    }
    this.errorMessageKey.set('');
    this.errorMessage.set(error instanceof Error ? error.message : fallback);
  }

}